import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@ui-nuvertos/shared';
import { Router } from '@angular/router';
import { getServiceUrl } from '../../utils/module-name-mapper';
export interface MasterFilters {
  limit?: number;
  offset?: number;
  search?: string;
  name?: string;
  code?: string;
  is_deleted?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class MastersService {
  private readonly API_BASE_URL = environment.accountingUrl;

  constructor(private http: HttpClient, private router: Router) {}


  /**
 * Get base href from index.html
 */
private getBaseHref(): string {
  const baseTag = document.querySelector('base');
  if (baseTag && baseTag.getAttribute('href')) {
    const baseHref = baseTag.getAttribute('href') || '/';
    return baseHref.endsWith('/') ? baseHref.slice(0, -1) : baseHref;
  }
  return '';
}

/**
 * Open Angular route in a new tab
 */
openInNewTab(route: string[], queryParams: any = {}): void {
  const baseHref = this.getBaseHref();

  const urlTree = this.router.createUrlTree(route, {
    queryParams
  });

  const serializedUrl = this.router.serializeUrl(urlTree);

  const pathWithBase = baseHref
    ? `${baseHref}${serializedUrl}`
    : serializedUrl;

  window.open(pathWithBase, '_blank', 'noopener,noreferrer');
}
  /**
   * Helper method to build query parameters
   */
  private buildParams(filters?: MasterFilters): HttpParams {
    let params = new HttpParams();

    if (filters) {
      if (filters.limit) {
        params = params.set('limit', filters.limit.toString());
      }

      if (filters.offset) {
        params = params.set('offset', filters.offset.toString());
      }

      if (filters.search) {
        params = params.set('search', filters.search);
      }
      if (filters.name) {
        params = params.set('name', filters.name);
      }
      if (filters.code) {
        params = params.set('code', filters.code);
      }
    }

    return params;
  }

  // Terms
  getTerms(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/terms/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getTermById(id: string | number): Observable<any> {
    return this.http.get<any>(`${this.API_BASE_URL}/api/v1/terms/${id}`, {
      withCredentials: true,
    });
  }

  createTerm(data: any): Observable<any> {
    return this.http.post<any>(`${this.API_BASE_URL}/api/v1/terms/`, data, {
      withCredentials: true,
    });
  }

  updateTerm(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(`${this.API_BASE_URL}/api/v1/terms/${id}`, data, {
      withCredentials: true,
    });
  }

  deleteTerm(id: string | number): Observable<any> {
    return this.http.delete<any>(`${this.API_BASE_URL}/api/v1/terms/${id}`, {
      withCredentials: true,
    });
  }

  // Vendor Type
  getVendorTypes(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/vendor-types/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getVendorTypeById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/vendor-types/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createVendorType(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/vendor-types/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateVendorType(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/vendor-types/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteVendorType(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/vendor-types/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // File Type
  getFileTypes(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/file-types/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getFileTypeById(id: string | number): Observable<any> {
    return this.http.get<any>(`${this.API_BASE_URL}/api/v1/file-types/${id}`, {
      withCredentials: true,
    });
  }

  createFileType(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/file-types/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateFileType(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/file-types/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteFileType(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/file-types/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Vendor Group
  getVendorGroups(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/vendor-groups/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getVendorGroupById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/vendor-groups/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createVendorGroup(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/vendor-groups/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateVendorGroup(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/vendor-groups/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteVendorGroup(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/vendor-groups/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Asset Group
  getAssetGroups(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/asset-groups/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getAssetGroupById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/asset-groups/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createAssetGroup(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/asset-groups/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateAssetGroup(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/asset-groups/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteAssetGroup(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/asset-groups/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  //Asset Sub Group
  getAssetSubGroups(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/asset-sub-groups/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getAssetSubGroupById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/asset-sub-groups/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createAssetSubGroup(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/asset-sub-groups/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateAssetSubGroup(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/asset-sub-groups/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteAssetSubGroup(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/asset-sub-groups/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Maintenance Type
  getMaintenanceTypes(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/maintenance-types/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getMaintenanceTypeById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/maintenance-types/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createMaintenanceType(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/maintenance-types/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateMaintenanceType(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/maintenance-types/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteMaintenanceType(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/maintenance-types/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Department
  getDepartments(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/departments/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getDepartmentById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/departments/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createDepartment(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/departments/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateDepartment(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/departments/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteDepartment(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/departments/${id}`,
      {
        withCredentials: true,
      }
    );
  }

   // Asset Write Off Reason
  getAssetWriteOffReasons(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/asset-write-off-reasons/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getAssetWriteOffReasonById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/asset-write-off-reasons/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createAssetWriteOffReason(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/asset-write-off-reasons/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateAssetWriteOffReason(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/asset-write-off-reasons/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteAssetWriteOffReason(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/asset-write-off-reasons/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Asset Maintenance Issue Type
  getAssetMaintenanceIssueTypes(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/asset-maintenance-issue-types/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getAssetMaintenanceIssueTypeById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/asset-maintenance-issue-types/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createAssetMaintenanceIssueType(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/asset-maintenance-issue-types/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateAssetMaintenanceIssueType(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/asset-maintenance-issue-types/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteAssetMaintenanceIssueType(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/asset-maintenance-issue-types/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Asset Maintenance Warranty Type
  getAssetMaintenanceWarrantyTypes(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/asset-maintenance-warranty-types/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getAssetMaintenanceWarrantyTypeById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/asset-maintenance-warranty-types/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createAssetMaintenanceWarrantyType(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/asset-maintenance-warranty-types/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateAssetMaintenanceWarrantyType(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/asset-maintenance-warranty-types/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteAssetMaintenanceWarrantyType(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/asset-maintenance-warranty-types/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Vendor Category
  getCategories(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/vendor-categories/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  // Account Categories
  getAccountCategories(): Observable<any> {
    return this.http.get(`${this.API_BASE_URL}/api/v1/account-categories/`, {
      withCredentials: true,
    });
  }

  getCategoryById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/vendor-categories/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createCategory(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/vendor-categories/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateCategory(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/vendor-categories/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteCategory(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/vendor-categories/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Nature of Document
  getNatureOfDocuments(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/nature-of-documents/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getNatureOfDocumentById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/nature-of-documents/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createNatureOfDocument(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/nature-of-documents/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateNatureOfDocument(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/nature-of-documents/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteNatureOfDocument(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/nature-of-documents/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // TDS Section
  getTdsSections(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/tds-sections/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getTdsSectionById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/tds-sections/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createTdsSection(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/tds-sections/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateTdsSection(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/tds-sections/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteTdsSection(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/tds-sections/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // TCS Section
  getTcsSections(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/tcs-sections/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getTcsSectionById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/tcs-sections/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createTcsSection(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/tcs-sections/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateTcsSection(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/tcs-sections/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteTcsSection(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/tcs-sections/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Chart of Accounts
  getChartOfAccounts(): Observable<any> {
    return this.http.get(
      `${this.API_BASE_URL}/api/v1/accounts/chart-of-accounts/`,
      {
        withCredentials: true,
      }
    );
  }

  // Get All Accounts
  getAllAccounts(params?: {
    [param: string]: string | string[];
  }): Observable<any> {
    const url = `${this.API_BASE_URL}/api/v1/accounts/all`;
    const httpOptions = {
      params: new HttpParams({ fromObject: params || {} }),
      withCredentials: true,
    };
    return this.http.get<any>(url, httpOptions);
  }

  // Get Chart of Accounts by Subsidiary ID
  // When onlyInactive is true, returns only inactive accounts
  // When onlyInactive is false (default), returns only active accounts
  getChartOfAccountsBySubsidiary(
    subsidiaryId: string | number,
    onlyInactive: boolean = false
  ): Observable<any> {
    const params = new HttpParams()
      .set('subsidiary_id', String(subsidiaryId))
      .set('only_inactive', String(onlyInactive));

    return this.http.get(
      `${this.API_BASE_URL}/api/v1/accounts/chart-of-accounts`,
      {
        params,
        withCredentials: true,
      }
    );
  }

  // Get Chart of Accounts with filters (subsidiary_id, account_type, account_type_id)
  getChartOfAccountsWithFilters(
    subsidiaryId: string | number,
    accountType: string = 'sub_account',
    accountTypeId?: string | number
  ): Observable<any> {
    let params = new HttpParams()
      .set('subsidiary_id', String(subsidiaryId))
      .set('account_type', accountType);

    if (accountTypeId) {
      params = params.set('account_type_id', String(accountTypeId));
    }

    return this.http.get(
      `${this.API_BASE_URL}/api/v1/accounts/chart-of-accounts`,
      {
        params,
        withCredentials: true,
      }
    );
  }

  getChartOfAccountById(id: string | number): Observable<any> {
    return this.http.get<any>(`${this.API_BASE_URL}/api/v1/accounts/${id}`, {
      withCredentials: true,
    });
  }

  createChartOfAccount(data: any): Observable<any> {
    return this.http.post<any>(`${this.API_BASE_URL}/api/v1/accounts/`, data, {
      withCredentials: true,
    });
  }

  updateChartOfAccount(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/accounts/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteChartOfAccount(id: string | number): Observable<any> {
    return this.http.delete<any>(`${this.API_BASE_URL}/api/v1/accounts/${id}`, {
      withCredentials: true,
    });
  }

  // Account Types
  getAccountTypes(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/account-types/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  // Fiscal Periods
  getFiscalPeriods(
    subsidiaryId?: string | number,
    status?: string,
    params?: any
  ): Observable<any> {
    let httpParams = new HttpParams();

    // Only add subsidiary_id if provided (optional filter)
    if (subsidiaryId !== undefined && subsidiaryId !== null) {
      httpParams = httpParams.set('subsidiary_id', String(subsidiaryId));
    }

    // Add status filter (backward compatible)
    if (status) {
      httpParams = httpParams.set('status', status);
    }

    // Add any additional params (including fiscal_year if needed)
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }

    return this.http.get(`${this.API_BASE_URL}/api/v1/fiscal-periods/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getFiscalPeriodById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/fiscal-periods/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createFiscalPeriod(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/fiscal-periods/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateFiscalPeriod(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/fiscal-periods/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteFiscalPeriod(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/fiscal-periods/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  /**
   * Update fiscal period status (AR/AP/JV)
   * @param id Fiscal period ID
   * @param statusType 'ar' | 'ap' | 'jv' - which status to update
   * @param newStatus 'open' | 'closed' | 'locked' - new status value
   */
  updateFiscalPeriodStatus(
    id: string | number,
    statusType: string,
    newStatus: string
  ): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/fiscal-periods/${id}/status`,
      {
        status_type: statusType,
        new_status: newStatus,
      },
      {
        withCredentials: true,
      }
    );
  }

  /**
   * Get fiscal period by date
   * @param subsidiaryId Subsidiary ID
   * @param date Date to find fiscal period for (YYYY-MM-DD format)
   */
  getFiscalPeriodByDate(
    subsidiaryId: string | number,
    date: string
  ): Observable<any> {
    const params = new HttpParams()
      .set('subsidiary_id', String(subsidiaryId))
      .set('date', date);

    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/fiscal-periods/by-date`,
      {
        params,
        withCredentials: true,
      }
    );
  }

  // Salt Strengths
  getSaltStrengths(filters?: MasterFilters): Observable<any> {
    const params = this.buildParams(filters);
    return this.http.get(`${this.API_BASE_URL}/api/v1/salt-strengths/`, {
      params,
      withCredentials: true,
    });
  }

  getSaltStrengthById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/salt-strengths/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Packaging Masters
  getPackagingMasters(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/packaging-masters/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getPackagingMasterById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/packaging-masters/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createPackagingMaster(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/packaging-masters/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updatePackagingMaster(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/packaging-masters/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deletePackagingMaster(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/packaging-masters/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Type Masters
  getTypeMasters(filters?: MasterFilters): Observable<any> {
    const params = this.buildParams(filters);
    return this.http.get(`${this.API_BASE_URL}/api/v1/type-masters/`, {
      params,
      withCredentials: true,
    });
  }

  getTypeMasterById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/type-masters/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Reason for Return
  getReasonForReturns(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/reason-for-returns/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getReasonForReturnById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/reason-for-returns/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createReasonForReturn(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/reason-for-returns/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateReasonForReturn(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/reason-for-returns/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteReasonForReturn(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/reason-for-returns/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Reason for Credit Note
  getReasonsForCreditNote(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(
      `${this.API_BASE_URL}/api/v1/reason-for-credit-notes/`,
      {
        params: httpParams,
        withCredentials: true,
      }
    );
  }

  getReasonForCreditNoteById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/reason-for-credit-notes/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createReasonForCreditNote(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/reason-for-credit-notes/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateReasonForCreditNote(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/reason-for-credit-notes/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteReasonForCreditNote(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/reason-for-credit-notes/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Shipping Method
  getShippingMethods(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/shipping-methods/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getShippingMethodById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/shipping-methods/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createShippingMethod(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/shipping-methods/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateShippingMethod(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/shipping-methods/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteShippingMethod(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/shipping-methods/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Refund Method
  getRefundMethods(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/refund-methods/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getRefundMethodById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/refund-methods/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createRefundMethod(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/refund-methods/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateRefundMethod(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/refund-methods/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteRefundMethod(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/refund-methods/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Bank Account
  getBankAccounts(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/bank-accounts/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getBankAccountById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/bank-accounts/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createBankAccount(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/bank-accounts/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateBankAccount(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/bank-accounts/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteBankAccount(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/bank-accounts/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Bank Name
  getBankNames(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/bank-names/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getBankNameById(id: string | number): Observable<any> {
    return this.http.get<any>(`${this.API_BASE_URL}/api/v1/bank-names/${id}`, {
      withCredentials: true,
    });
  }

  createBankName(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/bank-names/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateBankName(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/bank-names/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteBankName(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/bank-names/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Client Category
  getClientCategories(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/client-categories/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getClientCategoryById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/client-categories/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createClientCategory(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/client-categories/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateClientCategory(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/client-categories/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteClientCategory(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/client-categories/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Client Group
  getClientGroups(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/client-groups/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getClientGroupById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/client-groups/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createClientGroup(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/client-groups/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateClientGroup(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/client-groups/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteClientGroup(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/client-groups/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Contact Role
  getContactRoles(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/contact-roles/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getContactRoleById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/contact-roles/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createContactRole(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/contact-roles/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateContactRole(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/contact-roles/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteContactRole(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/contact-roles/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Line of Business
  getLinesOfBusiness(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/line-of-businesses/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getLineOfBusinessById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/line-of-businesses/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createLineOfBusiness(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/line-of-businesses/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateLineOfBusiness(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/line-of-businesses/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteLineOfBusiness(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/line-of-businesses/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Sales Type
  getSalesTypes(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/sales-types/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getSalesTypeById(id: string | number): Observable<any> {
    return this.http.get<any>(`${this.API_BASE_URL}/api/v1/sales-types/${id}`, {
      withCredentials: true,
    });
  }

  createSalesType(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/sales-types/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateSalesType(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/sales-types/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteSalesType(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/sales-types/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Shipping Carrier
  getShippingCarriers(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/shipping-carriers/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getShippingCarrierById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/shipping-carriers/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createShippingCarrier(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/shipping-carriers/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateShippingCarrier(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/shipping-carriers/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteShippingCarrier(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/shipping-carriers/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Cost Category
  getCostCategories(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/cost-categories/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getCostCategoryById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/cost-categories/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createCostCategory(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/cost-categories/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateCostCategory(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/cost-categories/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteCostCategory(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/cost-categories/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Expiry Category
  getExpiryCategories(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/expiry-categories/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getExpiryCategoryById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/expiry-categories/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createExpiryCategory(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/expiry-categories/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateExpiryCategory(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/expiry-categories/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteExpiryCategory(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/expiry-categories/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Item Type
  getItemTypes(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/type-masters/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getItemTypeById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/type-masters/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createItemType(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/type-masters/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateItemType(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/type-masters/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteItemType(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/type-masters/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Nature of Goods
  getNatureOfGoods(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/nature-of-goods/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getNatureOfGoodsById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/nature-of-goods/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createNatureOfGoods(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/nature-of-goods/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateNatureOfGoods(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/nature-of-goods/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteNatureOfGoods(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/nature-of-goods/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Storage Options
  getStorageOptions(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/storages/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getStorageOptionById(id: string | number): Observable<any> {
    return this.http.get<any>(`${this.API_BASE_URL}/api/v1/storages/${id}`, {
      withCredentials: true,
    });
  }

  createStorageOption(data: any): Observable<any> {
    return this.http.post<any>(`${this.API_BASE_URL}/api/v1/storages/`, data, {
      withCredentials: true,
    });
  }

  updateStorageOption(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/storages/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteStorageOption(id: string | number): Observable<any> {
    return this.http.delete<any>(`${this.API_BASE_URL}/api/v1/storages/${id}`, {
      withCredentials: true,
    });
  }

  // Manufacturers
  getManufacturers(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.API_BASE_URL}/api/v1/manufacturer-masters/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getManufacturerById(id: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/manufacturer-masters/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  createManufacturer(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/manufacturer-masters/`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  updateManufacturer(id: string | number, data: any): Observable<any> {
    return this.http.put<any>(
      `${this.API_BASE_URL}/api/v1/manufacturer-masters/${id}`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  deleteManufacturer(id: string | number): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/api/v1/manufacturer-masters/${id}`,
      {
        withCredentials: true,
      }
    );
  }

  // Generic CRUD methods - work for any master by API slug
  // When module is provided, routes to the module-specific service URL
  private getModuleBaseUrl(module?: string): string {
    return module
      ? getServiceUrl(module + '_', environment as Record<string, any>)
      : this.API_BASE_URL;
  }

  private getModulePathPrefix(module?: string): string {
    return module ? `${module}/` : '';
  }

  getAll(apiSlug: string, params?: any, module?: string): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    const baseUrl = this.getModuleBaseUrl(module);
    const pathPrefix = this.getModulePathPrefix(module);
    return this.http.get(`${baseUrl}/api/v1/${pathPrefix}${apiSlug}/`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getById(apiSlug: string, id: string | number, module?: string): Observable<any> {
    const baseUrl = this.getModuleBaseUrl(module);
    const pathPrefix = this.getModulePathPrefix(module);
    return this.http.get<any>(
      `${baseUrl}/api/v1/${pathPrefix}${apiSlug}/${id}`,
      { withCredentials: true }
    );
  }

  create(apiSlug: string, data: any, module?: string): Observable<any> {
    const baseUrl = this.getModuleBaseUrl(module);
    const pathPrefix = this.getModulePathPrefix(module);
    return this.http.post<any>(
      `${baseUrl}/api/v1/${pathPrefix}${apiSlug}/`,
      data,
      { withCredentials: true }
    );
  }

  update(apiSlug: string, id: string | number, data: any, module?: string): Observable<any> {
    const baseUrl = this.getModuleBaseUrl(module);
    const pathPrefix = this.getModulePathPrefix(module);
    return this.http.put<any>(
      `${baseUrl}/api/v1/${pathPrefix}${apiSlug}/${id}`,
      data,
      { withCredentials: true }
    );
  }

  remove(apiSlug: string, id: string | number, module?: string): Observable<any> {
    const baseUrl = this.getModuleBaseUrl(module);
    const pathPrefix = this.getModulePathPrefix(module);
    return this.http.delete<any>(
      `${baseUrl}/api/v1/${pathPrefix}${apiSlug}/${id}`,
      { withCredentials: true }
    );
  }

  // Get references for form fields
  getReferences(masterType: string, module?: string): Observable<any> {
    const baseUrl = this.getModuleBaseUrl(module);
    const pathPrefix = this.getModulePathPrefix(module);
    return this.http.get<any>(
      `${baseUrl}/api/v1/${pathPrefix}${masterType}/references`,
      {
        withCredentials: true,
      }
    );
  }

  // Get multiple masters at once
  getMasters(masterTypes: string[]): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/api/v1/masters/`,
      {
        masters: masterTypes,
      },
      {
        withCredentials: true,
      }
    );
  }

  // Get cities by state ID
  getCitiesByState(stateCode: string | number): Observable<any> {
    return this.http.get<any>(
      `${this.API_BASE_URL}/api/v1/cities?state_code=${stateCode}`,
      {
        withCredentials: true,
      }
    );
  }

  // Search cities by name with typeahead support
  // Fetches only matching cities from backend based on search query
  searchCities(query?: string, limit: number = 20): Observable<any> {
    let params = new HttpParams();
    if (query && query.trim().length >= 1) {
      params = params.set('search', query.trim());
    }
    if (limit && limit > 0) {
      params = params.set('limit', limit.toString());
    }
    return this.http.get<any>(`${this.API_BASE_URL}/api/v1/cities/`, {
      params,
      withCredentials: true,
    });
  }

  // Get city by ID
  getCityById(id: string | number): Observable<any> {
    return this.http.get<any>(`${this.API_BASE_URL}/api/v1/cities/${id}`, {
      withCredentials: true,
    });
  }

  // Sub Locations
  getSubLocations(params: any = {}): Observable<any> {
    let httpParams = new HttpParams();
    Object.keys(params).forEach((key) => {
      if (params[key] !== null && params[key] !== undefined) {
        httpParams = httpParams.set(key, params[key]);
      }
    });

    return this.http.get(`${this.API_BASE_URL}/api/v1/sub-locations/list`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  getSubLocationById(id: string | number): Observable<any> {
    return this.http.get(`${this.API_BASE_URL}/api/v1/sub-locations/${id}`, {
      withCredentials: true,
    });
  }

  createSubLocation(data: any): Observable<any> {
    return this.http.post(`${this.API_BASE_URL}/api/v1/sub-locations/create`, data, {
      withCredentials: true,
    });
  }

  updateSubLocation(id: string | number, data: any): Observable<any> {
    return this.http.put(`${this.API_BASE_URL}/api/v1/sub-locations/${id}`, data, {
      withCredentials: true,
    });
  }

  deleteSubLocation(id: string | number): Observable<any> {
    return this.http.delete(`${this.API_BASE_URL}/api/v1/sub-locations/${id}`, {
      withCredentials: true,
    });
  }

  /**
   * Export all masters to Excel file
   * Downloads a file with all master data in separate sheets
   */
  exportAllMasters(): void {
    this.http
      .get(`${this.API_BASE_URL}/api/v1/masters/export`, {
        withCredentials: true,
        responseType: 'blob',
      })
      .subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `masters_export_${new Date().toISOString().split('T')[0]}.xlsx`;
          link.click();
          window.URL.revokeObjectURL(url);
        },
        error: (error) => {
          console.error('Failed to export masters:', error);
        },
      });
  }
}

